import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import {
  Leaf, Check, X, Sparkles, ArrowLeft, Crown, Zap, GraduationCap
} from "lucide-react";
import { Input } from "../components/ui/input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PricingPage = () => {
  const navigate = useNavigate();
  const { user, login } = useAuth();
  const [isYearly, setIsYearly] = useState(false);
  const [loading, setLoading] = useState(null);
  const [showEduModal, setShowEduModal] = useState(false);
  const [eduEmail, setEduEmail] = useState("");
  const [eduCode, setEduCode] = useState("");
  const [eduStep, setEduStep] = useState(1);
  const [eduLoading, setEduLoading] = useState(false);

  const handleEduVerify = async () => {
    if (eduStep === 1) {
      // Request verification code
      setEduLoading(true);
      try {
        const response = await axios.post(`${API}/auth/verify-edu`, { email: eduEmail });
        toast.success("Verification code sent to your email!");
        if (response.data.dev_code) {
          // For dev: show the code
          toast.info(`Dev code: ${response.data.dev_code}`);
        }
        setEduStep(2);
      } catch (error) {
        toast.error(error.response?.data?.detail || "Please use a valid .edu email");
      } finally {
        setEduLoading(false);
      }
    } else {
      // Verify the code
      setEduLoading(true);
      try {
        await axios.post(`${API}/auth/verify-edu-code`, { email: eduEmail, code: eduCode });
        toast.success("Verified! You now have free premium access!");
        setShowEduModal(false);
        window.location.reload();
      } catch (error) {
        toast.error(error.response?.data?.detail || "Invalid code");
      } finally {
        setEduLoading(false);
      }
    }
  };

  const tiers = [
    {
      id: "free",
      name: "The Planner",
      tagline: "Plant your first seed of knowledge",
      monthlyPrice: 0,
      yearlyPrice: 0,
      features: [
        { text: "1 saved garden plan", included: true },
        { text: "Basic plant recommendations (top 5)", included: true },
        { text: "1 PDF export", included: true },
        { text: "5 plant identifications/month", included: true },
        { text: "Pet safety alerts (toxic plants)", included: true },
        { text: "Track 2 planted plants", included: true },
        { text: "3 seed types in seed bank", included: true },
      ],
      cta: "Start Growing Free",
      popular: false,
      icon: <Leaf className="w-6 h-6" />
    },
    {
      id: "standard",
      name: "The Gardener",
      tagline: "Watch your garden dreams flourish",
      monthlyPrice: 7,
      yearlyPrice: 70,
      includes: "Everything in The Planner, plus:",
      features: [
        { text: "Unlimited garden plans", included: true },
        { text: "Unlimited PDF exports", included: true },
        { text: "Advanced personalized recommendations", included: true },
        { text: "Companion & succession planting", included: true },
        { text: "Almanac-based planning", included: true },
        { text: "Garden journal with photos", included: true },
        { text: "Seasonal calendar & reminders", included: true },
        { text: "Unlimited plant identifications", included: true },
        { text: "Full seed stock tracker", included: true },
        { text: "Water sustainability tips", included: true },
      ],
      cta: "Cultivate Your Garden",
      popular: true,
      icon: <Zap className="w-6 h-6" />
    },
    {
      id: "premium",
      name: "Master Gardener",
      tagline: "Harvest the full potential of your land",
      monthlyPrice: 19,
      yearlyPrice: 171,
      includes: "Everything in The Gardener, plus:",
      features: [
        { text: "Infestation & health diagnoser", included: true },
        { text: "JSON + PDF data exports", included: true },
        { text: "Share plans with clients/friends", included: true },
        { text: "Real-time collaboration", included: true },
        { text: "Pro Gardener Chat (live expert)", included: true },
        { text: "Priority email support", included: true },
        { text: "Custom plant database entries", included: true },
        { text: "Harvest data tracking", included: true },
      ],
      cta: "Unlock Your Full Harvest",
      popular: false,
      icon: <Crown className="w-6 h-6" />
    }
  ];

  // Lifetime tier (separate, limited time offer)
  const lifetimeTier = {
    id: "lifetime",
    name: "Lifetime Master Gardener",
    tagline: "One payment, forever access",
    price: 89.99,
    isLifetime: true,
    includes: "Everything in Master Gardener, forever:",
    features: [
      { text: "All premium features, forever", included: true },
      { text: "Founding member badge", included: true },
      { text: "Priority support forever", included: true },
      { text: "No monthly or yearly fees", included: true },
      { text: "Future feature updates included", included: true },
    ],
    cta: "Get Lifetime Access",
    icon: <Sparkles className="w-6 h-6" />
  };

  const handleSubscribe = async (tierId, isLifetimeOption = false) => {
    if (tierId === "free") {
      if (!user) {
        login();
      } else {
        navigate('/create');
      }
      return;
    }

    if (!user) {
      login();
      return;
    }

    setLoading(tierId);

    try {
      const response = await axios.post(`${API}/subscriptions/checkout`, {
        tier: isLifetimeOption ? "lifetime" : tierId,
        billing_period: isLifetimeOption ? "lifetime" : (isYearly ? "yearly" : "monthly"),
        origin_url: window.location.origin
      }, { withCredentials: true });

      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout. Please try again.");
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Leaf className="w-8 h-8 text-primary" />
            <span className="font-serif text-xl font-bold text-primary">ROOTSPACE</span>
          </Link>
          <Link 
            to="/"
            className="text-muted-foreground hover:text-primary transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-foreground mb-4">
            Choose Your <span className="text-secondary">Growing</span> Plan
          </h1>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
            Start free and upgrade as your garden flourishes
          </p>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center gap-4">
            <Label htmlFor="billing" className={!isYearly ? "font-semibold" : "text-muted-foreground"}>
              Monthly
            </Label>
            <Switch 
              id="billing"
              checked={isYearly}
              onCheckedChange={setIsYearly}
              data-testid="billing-toggle"
            />
            <Label htmlFor="billing" className={isYearly ? "font-semibold" : "text-muted-foreground"}>
              Yearly
              <Badge className="ml-2 bg-green-100 text-green-700">Save 17%</Badge>
            </Label>
          </div>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {tiers.map((tier, index) => (
            <motion.div
              key={tier.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <Card 
                className={`card-organic h-full flex flex-col relative ${
                  tier.popular ? 'border-secondary border-2 shadow-lg' : ''
                } ${user?.subscription_tier === tier.id ? 'ring-2 ring-primary' : ''}`}
                data-testid={`pricing-card-${tier.id}`}
              >
                {tier.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary text-secondary-foreground">
                    <Sparkles className="w-3 h-3 mr-1" />
                    Most Popular
                  </Badge>
                )}
                
                {user?.subscription_tier === tier.id && (
                  <Badge className="absolute -top-3 right-4 bg-primary">
                    Current Plan
                  </Badge>
                )}

                <CardHeader className="text-center pb-4">
                  <div className={`w-14 h-14 rounded-xl flex items-center justify-center mx-auto mb-4 ${
                    tier.popular ? 'bg-secondary text-secondary-foreground' : 'bg-primary/10 text-primary'
                  }`}>
                    {tier.icon}
                  </div>
                  <CardTitle className="font-serif text-2xl">{tier.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">{tier.tagline}</p>
                </CardHeader>

                <CardContent className="flex-1">
                  <div className="text-center mb-6">
                    <span className="text-4xl font-bold text-foreground">
                      ${isYearly ? tier.yearlyPrice : tier.monthlyPrice}
                    </span>
                    {tier.monthlyPrice > 0 && (
                      <span className="text-muted-foreground">
                        /{isYearly ? 'year' : 'month'}
                      </span>
                    )}
                    {isYearly && tier.monthlyPrice > 0 && (
                      <p className="text-xs text-green-600 mt-1">
                        Save ${(tier.monthlyPrice * 12 - tier.yearlyPrice).toFixed(0)}/year
                      </p>
                    )}
                  </div>

                  {tier.includes && (
                    <div className="mb-4 pb-4 border-b border-border">
                      <p className="text-sm font-medium text-primary">{tier.includes}</p>
                    </div>
                  )}

                  <ul className="space-y-3">
                    {tier.features.map((feature, i) => (
                      <li 
                        key={i}
                        className={`flex items-start gap-2 text-sm ${
                          feature.included ? 'text-foreground' : 'text-muted-foreground line-through'
                        }`}
                      >
                        {feature.included ? (
                          <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        ) : (
                          <X className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        )}
                        {feature.text}
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter>
                  <Button
                    className={`w-full ${
                      tier.popular 
                        ? 'bg-secondary hover:bg-secondary/90 text-secondary-foreground' 
                        : 'btn-primary'
                    }`}
                    onClick={() => handleSubscribe(tier.id)}
                    disabled={loading === tier.id || user?.subscription_tier === tier.id}
                    data-testid={`subscribe-${tier.id}`}
                  >
                    {loading === tier.id ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                        Processing...
                      </span>
                    ) : user?.subscription_tier === tier.id ? (
                      "Current Plan"
                    ) : (
                      tier.cta
                    )}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Lifetime Tier - Limited Time Offer */}
        <motion.div
          className="mt-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <Card 
            className="card-organic bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 relative overflow-hidden"
            data-testid="pricing-card-lifetime"
          >
            <div className="absolute top-0 right-0 bg-red-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
              LIMITED TIME
            </div>
            <div className="flex flex-col md:flex-row items-center gap-6 p-6">
              <div className="flex-shrink-0">
                <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  {lifetimeTier.icon}
                </div>
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3 className="font-serif text-2xl font-bold text-amber-900">{lifetimeTier.name}</h3>
                <p className="text-amber-700 text-sm mb-2">{lifetimeTier.tagline}</p>
                <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                  {lifetimeTier.features.map((f, i) => (
                    <span key={i} className="text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full flex items-center gap-1">
                      <Check className="w-3 h-3" /> {f.text}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-center flex-shrink-0">
                <div className="text-4xl font-bold text-amber-900">${lifetimeTier.price}</div>
                <p className="text-amber-700 text-sm mb-3">one-time payment</p>
                <Button
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white px-8"
                  onClick={() => handleSubscribe("lifetime", true)}
                  disabled={loading === "lifetime" || user?.subscription_tier === "lifetime"}
                  data-testid="subscribe-lifetime"
                >
                  {loading === "lifetime" ? (
                    <span className="flex items-center gap-2">
                      <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      Processing...
                    </span>
                  ) : user?.subscription_tier === "lifetime" ? (
                    "Lifetime Member"
                  ) : (
                    lifetimeTier.cta
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </motion.div>

        {/* FAQ / Trust signals */}
        <motion.div 
          className="mt-16 text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <p className="text-muted-foreground text-sm">
            Cancel anytime • Secure payments via Stripe • 7-day money-back guarantee
          </p>
        </motion.div>

        {/* Student/Teacher Free Access */}
        <motion.div
          className="mt-8 p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl border border-green-200"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-green-800">Students & Teachers</h3>
                <p className="text-sm text-green-700">Get free Master Gardener access with your .edu email</p>
              </div>
            </div>
            <Button 
              onClick={() => setShowEduModal(true)}
              className="bg-green-600 hover:bg-green-700"
              data-testid="edu-verify-btn"
            >
              Verify .edu Email
            </Button>
          </div>
        </motion.div>

        {/* EDU Verification Modal */}
        {showEduModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-white rounded-2xl p-6 max-w-md w-full"
            >
              <div className="text-center mb-6">
                <GraduationCap className="w-12 h-12 mx-auto mb-3 text-green-600" />
                <h2 className="font-serif text-xl font-bold">Verify Educational Email</h2>
                <p className="text-sm text-muted-foreground mt-1">
                  {eduStep === 1 
                    ? "Enter your .edu email to receive a verification code" 
                    : "Enter the code sent to your email"}
                </p>
              </div>
              
              {eduStep === 1 ? (
                <div className="space-y-4">
                  <Input
                    type="email"
                    value={eduEmail}
                    onChange={(e) => setEduEmail(e.target.value)}
                    placeholder="your.name@university.edu"
                    data-testid="edu-email-input"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setShowEduModal(false)} className="flex-1">
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleEduVerify} 
                      disabled={!eduEmail.includes('.edu') || eduLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {eduLoading ? "Sending..." : "Send Code"}
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <Input
                    type="text"
                    value={eduCode}
                    onChange={(e) => setEduCode(e.target.value.toUpperCase())}
                    placeholder="Enter 8-digit code"
                    maxLength={8}
                    className="text-center text-lg tracking-widest"
                    data-testid="edu-code-input"
                  />
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setEduStep(1)} className="flex-1">
                      Back
                    </Button>
                    <Button 
                      onClick={handleEduVerify} 
                      disabled={eduCode.length < 8 || eduLoading}
                      className="flex-1 bg-green-600 hover:bg-green-700"
                    >
                      {eduLoading ? "Verifying..." : "Verify"}
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PricingPage;
